import { catalog } from "../i18n.js";

/** A bot's WhatsApp number: linking, its settings, and its conversations (components/WhatsApp.tsx). */
export const WHATSAPP = catalog(
  {
    badgePaused: "Paused",
    badgeConnected: "Connected",
    badgeLinking: "Linking",
    badgeBlocked: "Blocked",
    badgeNeedsAttention: "Needs attention",
    connected: "Connected",
    connectedAs: (number: string) => `Connected as ${number}`,
    warming: (connected: string, until: string) => `${connected} · new number, sending slowly until ${until}`,
    waitingForPhone: "Waiting for the phone to link…",
    giveNumber: (agent: string) => `Give ${agent} a number people can message when you're busy.`,

    channels: "Channels",
    channelsAbout: "Connect this bot to a messaging app so it can talk to people there.",
    connect: "Connect",
    fix: "Fix",
    open: "Open",
    conversations: (count: number) => `${count} conversation${count === 1 ? "" : "s"}`,
    waitingForYou: (count: number) => `${count} waiting for you`,

    reqNumber: (agent: string) => `A phone number only for ${agent}, not your own`,
    reqApp: "WhatsApp or WhatsApp Business installed on a phone with that number",
    reqOnline: "That phone online at least once every 14 days, or the link drops",
    beforeYouLink: "Before you link",
    caution: (agent: string) => `WhatsApp doesn't approve apps like this one and can block a number that behaves like a bot. Use a number you can afford to lose. ${agent} answers people who write to it, and messages people first only when you ask.`,
    disclaimer: "ark-bot isn't made by or affiliated with WhatsApp or Meta, and doesn't use the official WhatsApp Business API. You link this number at your own risk and are responsible for following WhatsApp's terms. If WhatsApp blocks or limits the number, ark-bot can't undo that and isn't responsible for it.",
    accept: (agent: string) => `This number is only for ${agent}, and I accept that WhatsApp may block it`,
    showCode: "Show the code",

    howToLink: "How to link",
    scanQr: "Scan a QR code",
    useCode: "Use a code instead",
    qrAlt: "QR code to link WhatsApp",
    gettingCode: "Getting a code…",
    noCode: "No code right now",
    codeChanges: "The code changes every 20 seconds or so.",
    newCode: "Get a new code",
    stepOpen: (agent: string) => `On ${agent}'s phone, open `,
    stepGoTo: "Go to ",
    menuLinkedDevices: "Settings → Linked devices",
    stepTap: "Tap ",
    linkDevice: "Link a device",
    stepPoint: " and point the phone at this code",
    phoneLabel: (agent: string) => `${agent}'s phone number, with the country code`,
    asking: "Asking…",
    getCode: "Get a code",
    askingWhatsApp: "Asking WhatsApp for a code…",
    codeLabel: (code: string) => `Code ${code}`,
    codeStepOn: (agent: string) => `On ${agent}'s phone: `,
    stepThen: ", then ",
    linkWithPhone: "Link with phone number instead",
    typeCode: "Type the code above",
    cancel: "Cancel",

    nobodyYet: "Nobody yet. Add people here, or from their conversation.",
    takeOff: (who: string) => `Take ${who} off the list`,
    remove: "Remove",
    namePlaceholder: "Name",
    theirName: "Their name",
    theirNumber: "Their number",
    add: "Add",
    stopAnsweringIn: (group: string) => `Stop answering in ${group}`,
    noGroupsYet: "No groups yet. ",
    groupsHint: (agent: string) => `When someone mentions ${agent} in another group, it shows up in Conversations without an answer. Allow the group there.`,

    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",

    calendarConnected: "Free times come from your Google Calendar. People only ever see the gaps, never what your meetings are.",
    calendarNotConnected: (agent: string) => `Connect Google Calendar in Plugins to use this. Until then, ${agent} takes a message when someone asks for a time.`,
    calendarNoPlugins: (agent: string) => `This box has no Plugins, so ${agent} can't see your calendar. People get your note and a message slip.`,
    checkingCalendar: "Checking your calendar…",
    audienceOff: "Nobody",
    audienceOffDetail: (agent: string) => `${agent} doesn't say when you're free, and takes a message instead.`,
    peopleYouKnow: "People you know",
    audienceKnownDetail: "Only people on your list.",
    audienceEveryone: "Everyone who writes",
    audienceEveryoneDetail: "Strangers hear free times too, but can't have a meeting held.",
    yourCalendar: "Your calendar",
    whoMayHear: "Who may hear when you're free",
    holdMeetings: "Hold meetings for people you know",
    holdMeetingsDetail: (agent: string) => `They pick a free time and ${agent} holds it. It goes in your calendar only when you press Book it.`,
    hoursLabel: "Hours you take meetings",
    daysLabel: "Days you take meetings",
    from: "From",
    to: "to",
    until: "Until",
    endAfterStart: "The end has to be after the start.",
    onYourClock: "On your clock. Nothing outside these hours is offered.",

    ownNumber: "Your own WhatsApp number",
    ownNumberHint: "Messages from this number are you, with everything you can do in the app.",
    knownAs: "How people know you",
    knownAsHint: (agent: string) => `So ${agent} can say whose assistant it is.`,
    unknownPeople: "People you don't know",
    takeMessage: "Take a message",
    takeMessageDetail: (agent: string) => `${agent} says it's your assistant, answers from your note, and passes the message on to you.`,
    stayQuiet: "Stay quiet",
    stayQuietDetail: "Only people on your list get a reply. The rest still show up in Conversations.",
    mayTell: (agent: string) => `What ${agent} may tell people`,
    notePlaceholder: "I'm usually free after 4 pm on weekdays. For anything about the office, Rina can help. Don't share my home address or where I am. Urgent: anything from my family.",
    noteHint: (agent: string) => `Besides your free times below, this is all ${agent} knows about you when it talks to someone else. Say what counts as urgent.`,
    everythingElse: "Everything else",
    urgentNow: "Tell me about urgent messages right away",
    urgentNowDetail: (agent: string) => `On your own phone, from ${agent}'s number`,
    urgentSwitch: "Urgent messages to your phone",
    answerGroups: "Answer in groups",
    answerGroupsDetail: (agent: string) => `Only in groups you allow, and only when someone mentions ${agent}. Everyone in the group reads the reply.`,
    groupsAnswers: (agent: string) => `Groups ${agent} answers in`,
    saving: "Saving…",
    save: "Save",
    savedNote: "Saved. It applies from the next message.",

    loading: "Loading…",
    notAvailable: "WhatsApp is not available on this box.",
    disconnect: "Disconnect",
    keep: "Keep",
    useAnother: "Use another number",
    linkAgain: "Link again",
    tryAgainNow: "Try again now",
    paneFoot: (agent: string) => `While WhatsApp is connected, ${agent}'s computer stays on so it can reply at any hour.`,

    tierKnown: "Known",
    tierUnknown: "Unknown",
    tierGroup: "Group",
    voiceNote: "Voice note",
    photo: "Photo",
    photoWith: (caption: string) => `Photo: ${caption}`,
    file: "File",
    fileWith: (name: string) => `File: ${name}`,
    unshowable: "Something WhatsApp sent that can't be shown here",
    callsThemselves: (name: string) => ` · calls themselves “${name}”`,
    yourNameForThem: "Your name for them",
    addToKnown: "Add to people I know",
    takeOffList: "Take off my list",
    groupNotAnswered: "Not answered here: this group isn't on your list. Mentions wait here until you allow it.",
    answerInGroup: "Answer in this group",
    stopInGroup: "Stop answering in this group",
    forYou: "for you · ",
    notDelivered: "not delivered · ",
    threadFootGroup: "Only you and the group see this conversation. The bot talks to each person separately and knows only your note here.",
    threadFoot: (name: string) => `Only you and ${name} see this conversation. The bot talks to each person separately and knows only your note here.`,
    nobodyWritten: "Nobody has written to this number yet.",
    notAnswered: "Not answered",
    replied: "Replied: ",
  },
  {
    badgePaused: "Dijeda",
    badgeConnected: "Terhubung",
    badgeLinking: "Menautkan",
    badgeBlocked: "Diblokir",
    badgeNeedsAttention: "Perlu dicek",
    connected: "Terhubung",
    connectedAs: (number: string) => `Terhubung sebagai ${number}`,
    warming: (connected: string, until: string) => `${connected} · nomor baru, kirim pelan-pelan sampai ${until}`,
    waitingForPhone: "Menunggu ponsel tertaut…",
    giveNumber: (agent: string) => `Beri ${agent} nomor yang bisa dihubungi orang saat kamu sibuk.`,

    channels: "Saluran",
    channelsAbout: "Hubungkan bot ini ke aplikasi pesan supaya bisa bicara dengan orang di sana.",
    connect: "Hubungkan",
    fix: "Perbaiki",
    open: "Buka",
    conversations: (count: number) => `${count} percakapan`,
    waitingForYou: (count: number) => `${count} menunggu kamu`,

    reqNumber: (agent: string) => `Nomor telepon khusus untuk ${agent}, bukan nomormu sendiri`,
    reqApp: "WhatsApp atau WhatsApp Business terpasang di ponsel dengan nomor itu",
    reqOnline: "Ponsel itu online setidaknya sekali tiap 14 hari, kalau tidak tautannya putus",
    beforeYouLink: "Sebelum menautkan",
    caution: (agent: string) => `WhatsApp tidak merestui aplikasi seperti ini dan bisa memblokir nomor yang terlihat seperti bot. Pakai nomor yang siap kamu relakan. ${agent} membalas orang yang menghubunginya, dan baru mengirim pesan duluan kalau kamu minta.`,
    disclaimer: "ark-bot tidak dibuat oleh atau berafiliasi dengan WhatsApp maupun Meta, dan tidak memakai WhatsApp Business API resmi. Kamu menautkan nomor ini atas risikomu sendiri dan bertanggung jawab mematuhi ketentuan WhatsApp. Kalau WhatsApp memblokir atau membatasi nomor ini, ark-bot tidak bisa membatalkannya dan tidak bertanggung jawab atasnya.",
    accept: (agent: string) => `Nomor ini khusus untuk ${agent}, dan saya terima kalau WhatsApp bisa memblokirnya`,
    showCode: "Tampilkan kodenya",

    howToLink: "Cara menautkan",
    scanQr: "Pindai kode QR",
    useCode: "Pakai kode saja",
    qrAlt: "Kode QR untuk menautkan WhatsApp",
    gettingCode: "Mengambil kode…",
    noCode: "Belum ada kode",
    codeChanges: "Kodenya berganti kira-kira tiap 20 detik.",
    newCode: "Minta kode baru",
    stepOpen: (agent: string) => `Di ponsel ${agent}, buka `,
    stepGoTo: "Buka ",
    menuLinkedDevices: "Setelan → Perangkat tertaut",
    stepTap: "Ketuk ",
    linkDevice: "Tautkan perangkat",
    stepPoint: " lalu arahkan ponsel ke kode ini",
    phoneLabel: (agent: string) => `Nomor ponsel ${agent}, dengan kode negara`,
    asking: "Meminta…",
    getCode: "Minta kode",
    askingWhatsApp: "Meminta kode ke WhatsApp…",
    codeLabel: (code: string) => `Kode ${code}`,
    codeStepOn: (agent: string) => `Di ponsel ${agent}: `,
    stepThen: ", lalu ",
    linkWithPhone: "Tautkan dengan nomor telepon saja",
    typeCode: "Ketik kode di atas",
    cancel: "Batal",

    nobodyYet: "Belum ada. Tambahkan orang di sini, atau dari percakapannya.",
    takeOff: (who: string) => `Hapus ${who} dari daftar`,
    remove: "Hapus",
    namePlaceholder: "Nama",
    theirName: "Nama mereka",
    theirNumber: "Nomor mereka",
    add: "Tambah",
    stopAnsweringIn: (group: string) => `Berhenti membalas di ${group}`,
    noGroupsYet: "Belum ada grup. ",
    groupsHint: (agent: string) => `Kalau ada yang menyebut ${agent} di grup lain, grup itu muncul di Percakapan tanpa balasan. Izinkan grupnya di sana.`,

    mon: "Sen",
    tue: "Sel",
    wed: "Rab",
    thu: "Kam",
    fri: "Jum",
    sat: "Sab",
    sun: "Min",

    calendarConnected: "Waktu luang diambil dari Google Calendar kamu. Orang hanya melihat waktu kosongnya, tidak pernah isi rapatmu.",
    calendarNotConnected: (agent: string) => `Hubungkan Google Calendar di Plugin untuk memakai ini. Sampai saat itu, ${agent} mencatat pesan kalau ada yang minta waktu.`,
    calendarNoPlugins: (agent: string) => `Box ini tidak punya Plugin, jadi ${agent} tidak bisa melihat kalendermu. Orang mendapat catatanmu, dan pesan mereka dicatat untukmu.`,
    checkingCalendar: "Mengecek kalendermu…",
    audienceOff: "Tidak ada",
    audienceOffDetail: (agent: string) => `${agent} tidak memberi tahu kapan kamu luang, dan mencatat pesan saja.`,
    peopleYouKnow: "Orang yang kamu kenal",
    audienceKnownDetail: "Hanya orang di daftarmu.",
    audienceEveryone: "Semua yang menghubungi",
    audienceEveryoneDetail: "Orang asing juga tahu waktu luangmu, tapi tidak bisa minta dijadwalkan rapat.",
    yourCalendar: "Kalender kamu",
    whoMayHear: "Siapa yang boleh tahu kapan kamu luang",
    holdMeetings: "Jadwalkan rapat untuk orang yang kamu kenal",
    holdMeetingsDetail: (agent: string) => `Mereka memilih waktu luang dan ${agent} menahannya untukmu. Rapatnya baru masuk ke kalendermu setelah kamu menekan Jadwalkan.`,
    hoursLabel: "Jam kamu menerima rapat",
    daysLabel: "Hari kamu menerima rapat",
    from: "Dari",
    to: "sampai",
    until: "Sampai",
    endAfterStart: "Jam selesai harus setelah jam mulai.",
    onYourClock: "Menurut jam kamu. Di luar jam ini tidak ada yang ditawarkan.",

    ownNumber: "Nomor WhatsApp kamu sendiri",
    ownNumberHint: "Pesan dari nomor ini dianggap dari kamu, dengan semua yang bisa kamu lakukan di aplikasi.",
    knownAs: "Nama yang dikenal orang",
    knownAsHint: (agent: string) => `Supaya ${agent} bisa bilang dia asisten siapa.`,
    unknownPeople: "Orang yang tidak kamu kenal",
    takeMessage: "Catat pesan",
    takeMessageDetail: (agent: string) => `${agent} bilang dia asistenmu, menjawab dari catatanmu, dan meneruskan pesannya ke kamu.`,
    stayQuiet: "Diam saja",
    stayQuietDetail: "Hanya orang di daftarmu yang dibalas. Sisanya tetap muncul di Percakapan.",
    mayTell: (agent: string) => `Yang boleh ${agent} sampaikan ke orang`,
    notePlaceholder: "Saya biasanya luang setelah jam 4 sore di hari kerja. Untuk urusan kantor, Rina bisa bantu. Jangan bagikan alamat rumah saya atau posisi saya. Mendesak: apa pun dari keluarga saya.",
    noteHint: (agent: string) => `Selain waktu luangmu di bawah, hanya ini yang ${agent} tahu tentang kamu saat bicara dengan orang lain. Tulis apa yang dianggap mendesak.`,
    everythingElse: "Lainnya",
    urgentNow: "Langsung kabari saya kalau ada pesan mendesak",
    urgentNowDetail: (agent: string) => `Ke ponselmu sendiri, dari nomor ${agent}`,
    urgentSwitch: "Pesan mendesak ke ponselmu",
    answerGroups: "Balas di grup",
    answerGroupsDetail: (agent: string) => `Hanya di grup yang kamu izinkan, dan hanya saat ada yang menyebut ${agent}. Semua anggota grup bisa membaca balasannya.`,
    groupsAnswers: (agent: string) => `Grup tempat ${agent} membalas`,
    saving: "Menyimpan…",
    save: "Simpan",
    savedNote: "Tersimpan. Berlaku mulai pesan berikutnya.",

    loading: "Memuat…",
    notAvailable: "WhatsApp tidak tersedia di box ini.",
    disconnect: "Putuskan",
    keep: "Biarkan",
    useAnother: "Pakai nomor lain",
    linkAgain: "Tautkan lagi",
    tryAgainNow: "Coba lagi sekarang",
    paneFoot: (agent: string) => `Selama WhatsApp terhubung, komputer ${agent} tetap menyala supaya bisa membalas kapan saja.`,

    tierKnown: "Dikenal",
    tierUnknown: "Tak dikenal",
    tierGroup: "Grup",
    voiceNote: "Pesan suara",
    photo: "Foto",
    photoWith: (caption: string) => `Foto: ${caption}`,
    file: "File",
    fileWith: (name: string) => `File: ${name}`,
    unshowable: "Kiriman WhatsApp yang tidak bisa ditampilkan di sini",
    callsThemselves: (name: string) => ` · menyebut dirinya “${name}”`,
    yourNameForThem: "Nama untuk dia",
    addToKnown: "Tambahkan ke orang yang saya kenal",
    takeOffList: "Hapus dari daftar saya",
    groupNotAnswered: "Tidak dibalas di sini: grup ini tidak ada di daftarmu. Mention-nya menunggu di sini sampai kamu izinkan grupnya.",
    answerInGroup: "Balas di grup ini",
    stopInGroup: "Berhenti membalas di grup ini",
    forYou: "untuk kamu · ",
    notDelivered: "tidak terkirim · ",
    threadFootGroup: "Hanya kamu dan grup itu yang melihat percakapan ini. Bot bicara dengan tiap orang secara terpisah dan hanya tahu catatanmu di sini.",
    threadFoot: (name: string) => `Hanya kamu dan ${name} yang melihat percakapan ini. Bot bicara dengan tiap orang secara terpisah dan hanya tahu catatanmu di sini.`,
    nobodyWritten: "Belum ada yang menghubungi nomor ini.",
    notAnswered: "Tidak dibalas",
    replied: "Dibalas: ",
  },
);
